import { Loggable } from '@rbi-ctg/frontend';
import { StatusType, dataDogLogger } from 'utils/datadog';

/**
 * Emits one structured Datadog log per partner-linking outcome (RP-Site view),
 * so the front-channel funnel is observable alongside the th-loyalty
 * back-channel logs: `info` for happy paths, `warn` for the rate-limit cap,
 * `error` for failures.
 *
 * The event name *is* the message, matching the `partnership.linking.*`
 * vocabulary ctg-th-loyalty-service logs (THLE-4369), so one query
 * (`@msg:partnership.linking*`) returns both legs. Prose messages cannot be
 * queried that way.
 *
 * `bypassSampling: true` keeps the funnel out of session log sampling
 * (`datadogLogSampleRate`), which would otherwise drop it in ~99% of sessions.
 */

type CtLinkingPhase = 'initiate-linking' | 'complete-linking';

/**
 * The closed set of outcomes. Names match the loyalty service's where the
 * outcome is the same, so one query counts both the client gate and the
 * gateway's refusal — `phase` and `blockedBy` tell them apart.
 *
 * An enum rather than free strings: dashboards are built on this vocabulary.
 */
export enum CtLinkingEvent {
  AttemptStarted = 'partnership.linking.attempt_started',
  /** A gate closed while an async step was in flight; `at` says which step. */
  AttemptAbandoned = 'partnership.linking.attempt_abandoned',
  FeatureDisabled = 'partnership.linking.feature_disabled',
  /** Canada-only block. `blockedBy` distinguishes the client gate from the gateway. */
  RegionNotEligible = 'partnership.linking.region_not_eligible',
  EmailVerificationRequired = 'partnership.linking.email_verification_required',
  /** The gateway's 24h LINKED cap. */
  RateLimited = 'partnership.linking.rate_limited',
  /** Gateway short-circuit — a success outcome, not a failure. */
  AlreadyLinked = 'partnership.linking.already_linked',
  AppTokenMalformed = 'partnership.linking.app_token_malformed',
  AppTokenExpired = 'partnership.linking.app_token_expired',
  /** Handing off to the partner. Pairs with a callback-leg event, or a drop-off. */
  OidcRedirect = 'partnership.linking.oidc_redirect',
  /** The partner reported an OAuth error on the callback URL. */
  OidcError = 'partnership.linking.oidc_error',
  InitiateFailed = 'partnership.linking.initiate_failed',
  /** Callback URL arrived without the `code` / `state` pair. */
  CallbackParamsMissing = 'partnership.linking.callback_params_missing',
  /** No stored verifier for the returned `state`: replay, CSRF, or a lost session. */
  PkceSessionExpired = 'partnership.linking.pkce_session_expired',
  CompleteFailed = 'partnership.linking.complete_failed',
  /**
   * THLE-4387 — CT refused the account itself, so no retry helps. Split from
   * `complete_failed` because it is terminal and gets its own screen.
   */
  CtAccountError = 'partnership.linking.ct_account_error',
  /** The mutation resolved, but with `success: false`. */
  CompleteUnsuccessful = 'partnership.linking.complete_unsuccessful',
  /** Linked. Same name the loyalty service uses for its own success. */
  RequestCompleted = 'partnership.linking.request_completed',
}

export type CtLinkingLogFields = Record<string, Loggable>;

interface ICtLinkingBindings extends CtLinkingLogFields {
  source?: string;
  /**
   * The id the loyalty service keys its own `partnership.*` logs on, so a
   * front-channel log joins the back-channel logs of the same link. `userId`
   * (the Cognito `sub`) cannot: only the server holds that mapping.
   */
  thLegacyCognitoId?: string;
  userId?: string;
}

/**
 * Field names that must never reach Datadog, matched case-insensitively after
 * stripping `_` / `-` so `authCode` and `auth_code` both hit.
 *
 * Both legs hold the OIDC `authCode`, the PKCE `codeVerifier`, and the handoff
 * JWT in locals beside their logging calls — how the loyalty service came to log
 * OIDC codes in the clear (THLE-4369). Matches names, not values: a secret
 * pasted into a free-text field such as `errorMessage` is not caught.
 */
const SENSITIVE_FIELDS: ReadonlySet<string> = new Set([
  'accesstoken',
  'apptoken',
  'authcode',
  'code',
  'codechallenge',
  'codeverifier',
  'idtoken',
  'refreshtoken',
  // The sessionStorage key the verifier is stored under, so it is as good as
  // the verifier on a replay — not a diagnostic, despite reading like one.
  'state',
  'token',
]);

/** Also drops `undefined`, so an unset optional diagnostic is absent, not empty. */
function redactFields(fields: CtLinkingLogFields): CtLinkingLogFields {
  const clean: CtLinkingLogFields = {};
  for (const [name, value] of Object.entries(fields)) {
    if (value !== undefined) {
      clean[name] = SENSITIVE_FIELDS.has(name.toLowerCase().replace(/[_-]/g, ''))
        ? 'redacted'
        : value;
    }
  }
  return clean;
}

/**
 * `bindings` are attached to every log the returned logger emits, in the shape
 * of `logger.child()` on the back channel: bind the identity once when the leg
 * starts instead of threading it through every call site.
 */
export function makeCtLinkingLogger(phase: CtLinkingPhase, bindings: ICtLinkingBindings = {}) {
  const bound = redactFields(bindings);

  const emit = (status: StatusType) => (
    event: CtLinkingEvent,
    context: CtLinkingLogFields = {}
  ): void => {
    dataDogLogger({
      message: event,
      status,
      bypassSampling: true,
      context: {
        feature: 'partner-linking',
        partnerId: 'CT',
        event,
        phase,
        ...bound,
        ...redactFields(context),
      },
    });
  };

  return {
    error: emit(StatusType.error),
    info: emit(StatusType.info),
    warn: emit(StatusType.warn),
  };
}
